import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, Typography, Button, Paper, Alert } from '@mui/material';
import { Lock as LockIcon, Home as HomeIcon } from '@mui/icons-material';
import usePermissions from '../hooks/usePermissions';

/**
 * ProtectedRoute component that checks user permissions before rendering children
 */
const ProtectedRoute = ({ 
  children, 
  requiredPermission = null,
  requiredPermissions = null,
  anyPermissions = null,
  requireAdmin = false,
  requireManager = false,
  fallbackPath = '/',
  showAccessDenied = true 
}) => {
  const { 
    hasPermission, 
    hasAnyPermission, 
    hasAllPermissions, 
    isAdmin, 
    isManager, 
    loading, 
    error,
    userRole 
  } = usePermissions();
  
  const location = useLocation();

  // Show loading while permissions are being fetched
  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="400px"
      >
        <Typography variant="h6">Loading permissions...</Typography>
      </Box>
    );
  }

  // Show error if permissions failed to load
  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Failed to load permissions: {error}
        </Alert>
      </Box>
    );
  }

  // Check permissions
  let hasAccess = true;
  let denialReason = '';

  // Single permission check
  if (requiredPermission && !hasPermission(requiredPermission)) {
    hasAccess = false;
    denialReason = `Missing required permission: ${requiredPermission}`;
  }

  // Multiple permissions (all required)
  if (requiredPermissions && !hasAllPermissions(requiredPermissions)) {
    hasAccess = false;
    denialReason = `Missing required permissions: ${requiredPermissions.join(', ')}`;
  }

  // Any of the permissions
  if (anyPermissions && !hasAnyPermission(anyPermissions)) {
    hasAccess = false;
    denialReason = `Missing any of required permissions: ${anyPermissions.join(', ')}`;
  }

  // Admin requirement
  if (requireAdmin && !isAdmin) {
    hasAccess = false;
    denialReason = 'Administrator access required';
  }

  // Manager requirement
  if (requireManager && !isManager) {
    hasAccess = false;
    denialReason = 'Manager-level access required';
  }

  // If user has access, render the protected content
  if (hasAccess) {
    return children;
  }

  // If no access and showAccessDenied is false, redirect
  if (!showAccessDenied) {
    return <Navigate to={fallbackPath} state={{ from: location }} replace />;
  }

  // Show access denied page
  return (
    <AccessDeniedPage 
      reason={denialReason}
      userRole={userRole}
      currentPath={location.pathname}
      fallbackPath={fallbackPath}
    />
  );
};

/**
 * Access Denied Page Component
 */
const AccessDeniedPage = ({ reason, userRole, currentPath, fallbackPath }) => {
  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="60vh"
      p={3}
    >
      <Paper 
        elevation={3} 
        sx={{ 
          p: 4, 
          textAlign: 'center', 
          maxWidth: 500,
          width: '100%'
        }}
      >
        <LockIcon 
          sx={{ 
            fontSize: 64, 
            color: 'error.main', 
            mb: 2 
          }} 
        />
        
        <Typography variant="h4" gutterBottom color="error">
          Access Denied
        </Typography>
        
        <Typography variant="body1" paragraph color="text.secondary">
          You don't have permission to access this page.
        </Typography>
        
        <Alert severity="warning" sx={{ mb: 3, textAlign: 'left' }}>
          <Typography variant="body2">
            <strong>Reason:</strong> {reason}
          </Typography>
          <Typography variant="body2">
            <strong>Your Role:</strong> {userRole || 'Unknown'}
          </Typography>
          <Typography variant="body2">
            <strong>Requested Page:</strong> {currentPath}
          </Typography>
        </Alert>

        <Typography variant="body2" paragraph>
          If you believe this is an error, please contact your system administrator.
        </Typography>

        <Box display="flex" gap={2} justifyContent="center">
          <Button
            variant="contained"
            startIcon={<HomeIcon />}
            onClick={() => window.location.href = fallbackPath}
          >
            Go to Dashboard
          </Button>
          
          <Button
            variant="outlined"
            onClick={() => window.history.back()}
          >
            Go Back
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

/**
 * Higher-order component for protecting routes
 */
export const withPermission = (
  WrappedComponent, 
  permissionConfig = {}
) => {
  return (props) => (
    <ProtectedRoute {...permissionConfig}>
      <WrappedComponent {...props} />
    </ProtectedRoute>
  );
};

/**
 * Hook for programmatic navigation with permission checking
 */
export const usePermissionNavigation = () => {
  const { canAccessPage, hasPermission } = usePermissions();

  const navigateWithPermissionCheck = (path, requiredPermission) => {
    if (requiredPermission && !hasPermission(requiredPermission)) {
      console.warn(`Navigation denied: Missing permission ${requiredPermission} for ${path}`);
      return false;
    }
    
    window.location.href = path;
    return true;
  };

  return { navigateWithPermissionCheck, canAccessPage, hasPermission };
};

export default ProtectedRoute; 